import { Box, Typography } from "@mui/material";
import { InfoOutline } from "@mui/icons-material";
import { Formik } from "formik";
import { BankSchema } from "shared/helpers/Schema";
import { useDispatch, useSelector } from "react-redux";
import { ProfileActions } from "state/profile/Action";
import { LoanService } from "services/LoanService";
import Utils from "shared/helpers/Utils";
import { useProgress } from "context/ProgressProvider";
import {
  AppFormikTextField,
  AppPrimaryButton,
  BlackTooltip,
} from "shared/components/AppComponents";

const BankDetails = () => {
  const dispatch = useDispatch();
  const { handleCompleteAndNext, currentProfile, isBroker } = useProgress();

  const generateDocumentMapping = (callback: any): void => {
    LoanService.generateDocumentMapping({ loanId: currentProfile.loadId })
      .then((response: any) => {
        callback(true);
      })
      .catch((error: any) => {})
      .finally(() => {});
  };

  const handleAddBankDetails = (value, formik) => {
    if (currentProfile.isAllowedToEdit) {
      if (!currentProfile.isAllowedToEdit?.bankDetails) {
        handleCompleteAndNext();
        return;
      }
    }

    LoanService.addBankDetails(
      {
        BSB: value.BSB.toString(),
        accountName: value.accountName,
        accountNumber: value.accountNumber.toString(),
      },
      currentProfile.loadId,
    )
      .then((response: any) => {
        if (!currentProfile.isDocumentMappingCompleted) {
          generateDocumentMapping((status: any) => {
            if (status) {
              handleCompleteAndNext();
            }
          });
        } else {
          handleCompleteAndNext();
        }
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        formik?.setSubmitting(false);
      });
  };

  const handleSubmitForm = (value, formik) => {
    formik?.setSubmitting(true);
    console.log(value);
    dispatch(ProfileActions.setRndProfile(value));
    handleAddBankDetails(value, formik);
  };

  return (
    <>
      <Box>
        <Typography variant="h2">
          Your bank details{" "}
          <BlackTooltip
            title={
              isBroker
                ? "Used to transfer the settlement funds once the process is complete."
                : "Used to transfer your settlement funds once the process is complete."
            }
          >
            <InfoOutline fontSize={"small"} sx={{ marginLeft: "10px" }} />
          </BlackTooltip>
        </Typography>
        <Typography variant="h3" fontWeight={400}>
          {isBroker ? (
            <>
              Please add your customer bank details for us to disburse the funds
            </>
          ) : (
            <> Please add your bank details for us to disburse your funds</>
          )}
        </Typography>

        <Box sx={{ marginTop: "2rem" }}></Box>
        <Formik
          enableReinitialize
          validationSchema={BankSchema}
          validateOnMount
          onSubmit={(formValues, formik) => {
            handleSubmitForm(formValues, formik);
          }}
          initialValues={{
            BSB: currentProfile.BSB,
            accountNumber: currentProfile.accountNumber,
            accountName: currentProfile.accountName,
          }}
        >
          {({ handleSubmit, errors, isSubmitting }) => {
            console.log(errors);
            return (
              <form autoComplete="off">
                <Box mt={3}>
                  <AppFormikTextField
                    placeholder="BSB"
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box>BSB</Box>
                        <BlackTooltip
                          title={
                            isBroker
                              ? "The 6-digit Bank State Branch (BSB) number for your customer account."
                              : "The 6-digit Bank State Branch (BSB) number for your account."
                          }
                        >
                          <InfoOutline fontSize={"small"} />
                        </BlackTooltip>
                      </Box>
                    }
                    name="BSB"
                    outerLabel
                    className="ezifin-default-field"
                  />
                </Box>

                <Box mt={3}>
                  <AppFormikTextField
                    placeholder="Account number"
                    label="Account number"
                    name="accountNumber"
                    outerLabel
                    className="ezifin-default-field"
                  />
                </Box>

                <Box mt={3}>
                  <AppFormikTextField
                    placeholder="Account name"
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box>Account name</Box>
                        <BlackTooltip title="The full name the bank account is held in.">
                          <InfoOutline fontSize={"small"} />
                        </BlackTooltip>
                      </Box>
                    }
                    name="accountName"
                    outerLabel
                    className="ezifin-default-field"
                  />
                </Box>

                <Box mt={3}>
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={isSubmitting}
                    loadingText="Please wait.."
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSubmit();
                    }}
                    disabled={Object.keys(errors).length > 0 || isSubmitting}
                  />
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
    </>
  );
};

export default BankDetails;
